import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { apiBase, authHeaders } from '../../utils/authHeaders';
import { 
  Home, Package, Calendar, Truck, Shield, Users,
  Settings, User, Bell, Wifi, WifiOff
} from 'lucide-react';

const MobileNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, isOnline, getMobileNavigation, token } = useMobileAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    const locationId = localStorage.getItem('current_location_id') || '';
    if (!token || !locationId) { setUnreadCount(0); return; }
    try {
      const response = await fetch(`${apiBase()}/api/mobile/notifications/unread-count`, {
        headers: authHeaders({ token, locationId }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if ((localStorage.getItem('current_location_id') || '') === locationId) setUnreadCount(Number(data.unread_count) || 0);
    } catch { /* Inbox remains available even when its badge cannot refresh. */ }
  }, [token]);

  useEffect(() => {
    loadUnreadCount();
    window.addEventListener('locationChanged', loadUnreadCount);
    window.addEventListener('organizationChanged', loadUnreadCount);
    window.addEventListener('storage', loadUnreadCount);
    window.addEventListener('notificationsChanged', loadUnreadCount);
    window.addEventListener('focus', loadUnreadCount);
    return () => {
      window.removeEventListener('locationChanged', loadUnreadCount);
      window.removeEventListener('organizationChanged', loadUnreadCount);
      window.removeEventListener('storage', loadUnreadCount);
      window.removeEventListener('notificationsChanged', loadUnreadCount);
      window.removeEventListener('focus', loadUnreadCount);
    };
  }, [loadUnreadCount]);

  const navigationItems = getMobileNavigation();

  const getIcon = (iconName) => {
    const icons = {
      home: Home,
      package: Package,
      calendar: Calendar,
      truck: Truck,
      shield: Shield,
      users: Users,
      settings: Settings,
      user: User,
      bell: Bell
    };
    const Icon = icons[iconName] || Home;
    return <Icon className="w-5 h-5" />;
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="bg-white border-t border-gray-200">
      {/* Connection Status Bar */}
      <div className={`px-4 py-1 flex items-center justify-between text-xs ${
        isOnline ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
      }`}>
        <div className="flex items-center space-x-1">
          {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          <span>{isOnline ? 'Connected' : 'Offline'}</span>
        </div>
        <span className="text-xs opacity-75">
          {user?.displayName || 'User'} • {role.replace('_', ' ')}
        </span>
      </div>

      {/* Navigation Items */}
      <div className="flex justify-around py-2">
        {navigationItems.map((item) => {
          const active = isActive(item.path);
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors ${
                active
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`mb-1 ${active ? 'text-blue-600' : 'text-gray-500'}`}>
                {getIcon(item.icon)}
              </div>
              <span className={`text-xs font-medium ${
                active ? 'text-blue-600' : 'text-gray-500'
              }`}>
                {item.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
        <div className="flex justify-around">
          <button
            onClick={() => navigate('/mobile/profile')}
            className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800"
          >
            <User className="w-4 h-4" />
            <span>Profile</span>
          </button>
          
          <button
            onClick={() => { navigate('/mobile/notifications'); loadUnreadCount(); }}
            className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800 relative"
          >
            <Bell className="w-4 h-4" />
            <span>Alerts</span>
            {unreadCount > 0 && <span aria-label={`${unreadCount} unread notifications`} className="absolute -top-1 -right-2 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] leading-4 text-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
          
          <button
            onClick={() => navigate('/mobile/settings')}
            className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800"
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileNavigation;
